import ChainaioQuoteAvatar from '@public/images/avatars/chain-io-brian-glick.png'
import FortoQuoteAvatar from '@public/images/avatars/forto-nina-gontgen-voss.png'
import JasQuoteAvatar from '@public/images/avatars/jas-andrea-goeman.png'
import LogixboardQuoteAvatar from '@public/images/avatars/logixboard-julian-alvarez.png'
import VAlexanderQuoteAvatar from '@public/images/avatars/v-alexander-daniel-hallock.png'
import ChainioQuoteImage from '@public/images/customers/tab-quotes/tab-2/chain-io.png'
import FortoQuoteImage from '@public/images/customers/tab-quotes/tab-2/forto.png'
import JasQuoteImage from '@public/images/customers/tab-quotes/tab-2/jas.png'
import LogixboardQuoteImage from '@public/images/customers/tab-quotes/tab-2/logixboard.png'
import VAlexanderQuoteImage from '@public/images/customers/tab-quotes/tab-2/v-alexander.png'
import ChainioLogo from '@public/images/logos/chainio.png'
import FortoLogo from '@public/images/logos/forto.png'
import JasLogo from '@public/images/logos/jas.svg'
import LogixboardLogo from '@public/images/logos/logixboard.png'
import VAlexanderLogo from '@public/images/logos/v-alexander.png'
import { useState } from 'react'

import TwoColumnLayoutWithTabs, { TabDetails } from '@/components/TwoColumnLayoutWithTabs'

enum Customers {
    JAS = 'JAS',
    FORTO = 'Forto',
    CHAINIO = 'Chain.io',
    LOGIXBOARD = 'Logixboard',
    V_ALEXANDER = 'V Alexander',
}

export type CustomerDetails = Record<Customers, TabDetails<Customers>>

const customerDetails: CustomerDetails = {
    JAS: {
        name: Customers.JAS,
        logo: JasLogo,
        description: (
            <>
                JAS Worldwide wanted to go beyond reducing CO₂ emissions in freight by empowering
                customers to contribute to impactful climate projects globally.
                <br />
                <br />
                To navigate this commitment with credibility, they chose to partner with Lune
                because of our shared commitment to transparency and positive impact.
            </>
        ),
        quoteImage: JasQuoteImage,
        quoteName: `Andrea Goeman`,
        quoteTitle: `SVP QHSE & Sustainability at JAS Worldwide`,
        quoteAvatar: JasQuoteAvatar,
        quoteText: (
            <>
                By offering our customers not only biofuel insetting and network optimization
                solutions, but also offsetting opportunities through our partnership with Lune, we
                help them achieve their environmental goals while keeping our promise to make a
                positive impact.
            </>
        ),
        chips: [`climateProjects`],
        chipColor: `#FBF0F4`,
    },
    Forto: {
        name: Customers.FORTO,
        logo: FortoLogo,
        description: (
            <>
                Forto saw unmet demand for sustainable logistics, driven by their own strategic
                vision as well as tightening regulations in the logistics field.
                <br />
                <br />
                By integrating Lune’s API, Forto is able to offer a cutting edge sustainable
                logistics product to grow their business and delight their customers.
            </>
        ),
        quoteImage: FortoQuoteImage,
        quoteName: `Nina Göntgen-Voss`,
        quoteTitle: `Director of Sustainability at Forto`,
        quoteAvatar: FortoQuoteAvatar,
        quoteText: (
            <>
                We partnered with Lune because of the level of granularity in their emissions
                calculations and the flexibility their API provides. We assessed several providers
                and Lune is truly challenging the status quo and revolutionising the carbon markets.
            </>
        ),
        chips: [`climateProjects`, `emissionCalculations`],
        chipColor: `#D2EEFF`,
    },
    [`Chain.io`]: {
        name: Customers.CHAINIO,
        logo: ChainioLogo,
        description: (
            <>
                Chain.io has understood the challenges associated with complex scope 3 emissions
                reporting and regulatory compliance for their customers.
                <br />
                <br />
                By integrating the Lune API, customers can now easily fulfill market and regulatory
                requirements for emissions visibility and greener supply chains without every
                leaving their TMS.
            </>
        ),
        quoteImage: ChainioQuoteImage,
        quoteName: `Brian Glick`,
        quoteTitle: `CEO at Chain.io`,
        quoteAvatar: ChainaioQuoteAvatar,
        quoteText: (
            <>
                Emissions data cannot be an afterthought and must be built into all aspects of a
                shipment’s lifecycle. Environmental impact is driving shipper behaviour from
                purchasing through routing, and should be front-and-center for every logistics
                service provider.
            </>
        ),
        chips: [`emissionCalculations`],
        chipColor: `#E3F1E8`,
    },
    Logixboard: {
        name: Customers.LOGIXBOARD,
        logo: LogixboardLogo,
        description: (
            <>
                Logixboard wanted to give their customers visibility into and control over their
                scope 3 emissions and tools to take action — all within their logistics platform.
                <br />
                <br />
                They integrated the Lune API to quickly introduce automatic emissions calculations
                and high quality carbon offsetting into their customer experience.
            </>
        ),
        quoteImage: LogixboardQuoteImage,
        quoteName: `Julian Alvarez`,
        quoteTitle: `CEO and Founder at Logixboard`,
        quoteAvatar: LogixboardQuoteAvatar,
        quoteText: (
            <>
                CO₂ emission tracking is becoming more important for logistics businesses globally,
                especially those with larger brand-conscious clients. We are seeing CO₂ tracking as
                a requirement in RFQs/RFPs, and we know that many shippers are facing pressure from
                their consumers to ‘go green.
            </>
        ),
        chips: [`emissionCalculations`],
        chipColor: `#ECE3D2`,
    },
    [`V Alexander`]: {
        name: Customers.V_ALEXANDER,
        logo: VAlexanderLogo,
        description: (
            <>
                By partnering with Lune, V. Alexander is pioneering green logistics in the US.
                <br />
                <br />
                Its customers can now use the new granular emission calculations to report and
                comply with climate regulations, pinpoint emission hotspots for decarbonisation, and
                accelerate their sustainability goals.
            </>
        ),
        quoteImage: VAlexanderQuoteImage,
        quoteName: `Julian Alvarez`,
        quoteTitle: `CEO and Founder at Logixboard`,
        quoteAvatar: VAlexanderQuoteAvatar,
        quoteText: (
            <>
                Our partnership with Lune reflects V. Alexander’s commitment to our customers. We
                live in a global economy, so many are grappling with EU and California climate
                regulations. By providing shipment emission calculations we are helping them comply
                and decarbonise.
            </>
        ),
        chips: [`emissionCalculations`],
        chipColor: `#C2DEEC`,
    },
}

const CustomersTab2 = () => {
    const [selectedCustomer, setSelectedCustomer] = useState<Customers>(Customers.JAS)

    return (
        <TwoColumnLayoutWithTabs
            values={customerDetails}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
        />
    )
}

export default CustomersTab2
